import React, { useEffect, useState } from "react";
import { Avatar, Box, Card, CircularProgress, Grid, Typography } from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { MAIN_THEME_COLOR, SECONDARY_THEME_COLOR } from "../../providers/theme/colors/colors";
import { firestore } from "../../index";
import {
  defaultEditorialBoard,
  EditorialCommitteeMember,
  sortEditorialCommitteeMembers,
} from "./yuvagaaz_data";

export default function EditorailBoard() {
  const [members, setMembers] = useState<EditorialCommitteeMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchMembers() {
      try {
        const snapshot = await getDocs(collection(firestore, "yuvagaaz_editorial_committee"));
        const loadedMembers = sortEditorialCommitteeMembers(
          snapshot.docs.map((memberDoc) => ({
            docId: memberDoc.id,
            ...(memberDoc.data() as Omit<EditorialCommitteeMember, "docId">),
          })),
        );
        setMembers(
          loadedMembers.length
            ? loadedMembers
            : sortEditorialCommitteeMembers(defaultEditorialBoard),
        );
      } catch (error) {
        console.error("Failed to load Yuvagaaz editorial committee", error);
        setMembers(sortEditorialCommitteeMembers(defaultEditorialBoard));
      } finally {
        setLoading(false);
      }
    }

    fetchMembers();
  }, []);

  return (
    <Box my={6} px={{ xs: 1, sm: 0 }} id="editorialBoard">
      <Box mb={3} textAlign="center">
        <Typography
          variant="h5"
          fontWeight={800}
          color={MAIN_THEME_COLOR}
          gutterBottom
          sx={{ fontSize: { xs: 26, sm: 30 } }}
        >
          Editorial Board
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: { xs: 14, sm: 16 } }}
        >
          Yuvagaaz, Youth Magazine - Diocese of Udupi
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3} justifyContent="center">
          {members.map((member, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={index < 6 ? 4 : 3}
              lg={index < 6 ? 4 : 3}
              key={member.docId || `${member.name}-${member.post}`}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: { xs: 3, md: 4 },
                  border: `1px solid rgba(56, 136, 194, 0.16)`,
                  boxShadow: "0 14px 30px rgba(0, 0, 0, 0.08)",
                  overflow: "hidden",
                  background:
                    "linear-gradient(180deg, rgba(244, 250, 255, 0.95) 0%, rgba(255, 255, 255, 1) 100%)",
                }}
              >
                <Box
                  sx={{
                    height: 8,
                    background: `linear-gradient(90deg, ${MAIN_THEME_COLOR} 0%, ${SECONDARY_THEME_COLOR} 100%)`,
                  }}
                />
                <Box
                  px={{ xs: 1.75, md: 2 }}
                  py={{ xs: 2.5, md: 3 }}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  height="100%"
                >
                  {member.img ? (
                    <Box
                      component="img"
                      src={member.img}
                      alt={member.name}
                      sx={{
                        width: { xs: 150, sm: 170, md: 180 },
                        height: { xs: 180, sm: 200, md: 215 },
                        mb: 2,
                        borderRadius: 3,
                        objectFit: "cover",
                        objectPosition: "center top",
                        border: `4px solid ${SECONDARY_THEME_COLOR}`,
                        boxShadow: "0 12px 24px rgba(56, 136, 194, 0.16)",
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: { xs: 150, sm: 170, md: 180 },
                        height: { xs: 180, sm: 200, md: 215 },
                        mb: 2,
                        borderRadius: 3,
                        bgcolor: `${SECONDARY_THEME_COLOR}33`,
                        color: MAIN_THEME_COLOR,
                        border: `4px solid ${SECONDARY_THEME_COLOR}`,
                        boxShadow: "0 12px 24px rgba(56, 136, 194, 0.16)",
                        fontSize: { xs: "2.6rem", md: "3.1rem" },
                        fontWeight: 700,
                      }}
                    >
                      {member.name.charAt(0)}
                    </Avatar>
                  )}
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ mb: 0.5, fontSize: { xs: 18, md: 20 } }}
                  >
                    {member.name}
                  </Typography>
                  {member.place ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 0.75, lineHeight: 1.5, textAlign: "center" }}
                    >
                      {member.place}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{
                      lineHeight: 1.6,
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      textAlign: "center",
                    }}
                  >
                    {member.post}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
